import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import useTheme from '@mui/material/styles/useTheme'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { enqueueSnackbar } from 'notistack'
import { useCallback, useMemo, useState } from 'react'
import { useDropzone } from 'react-dropzone'

import { i18WithParams as t } from '@shared/locale'
import { formatDateSimple } from 'components-ui/src/shared/dates'
import { imageResizer } from 'components-ui/src/shared/image_resizer'
import { captureError } from 'service-api/src/shared/monitoring'
import { LocationPetProfileClientDto } from 'service-api/src/types/api'

interface TPetVaccinesRecordsFormProps {
  onFileUpload: (files: File[]) => void
  onFileRemove: (file: File) => void
  lpp?: LocationPetProfileClientDto
}

const formatFileSize = (bytes: number) => `${Math.max(1, Math.round(bytes / (1024 * 1024)))}mb`

export const PetVaccinesRecordsForm = ({ onFileUpload, onFileRemove, lpp }: TPetVaccinesRecordsFormProps) => {
  const theme = useTheme()
  const isGreaterThanMD = useMediaQuery(theme.breakpoints.up('md'))
  const [files, setFiles] = useState<File[]>([])

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setFiles((prevValue) => [...prevValue, ...acceptedFiles])
      onFileUpload(acceptedFiles)
    },
    [onFileUpload]
  )

  const onDropRejected = useCallback(() => {
    enqueueSnackbar(t('detailsPolicies.vaccinationRecords.upload.error'), {
      variant: 'error',
    })
    captureError({ cause: t('detailsPolicies.vaccinationRecords.upload.error') } as Error)
  }, [])

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    onDropRejected,
    accept: {
      'image/jpeg': [],
      'image/jpg': [],
      'image/png': [],
      'application/pdf': [],
    },
    noClick: true,
    maxSize: 9000000,
  })

  const onRemoveHandler = (file: File) => {
    setFiles((prevValue) => prevValue.filter((f) => f !== file))
    onFileRemove(file)
  }

  const records = useMemo(() => {
    const recs = lpp?.attachments || []
    if (recs.length === 0) {
      return []
    }

    return recs
      .filter((rec) => rec.subtype === 'VET_RECORD')
      .sort((a, b) => (a.createdAt! > b.createdAt! ? -1 : 1))
      .slice(0, 5)
  }, [lpp?.attachments])

  return (
    <>
      <Typography variant="h6" sx={{ mt: 1, mb: 3 }}>
        {t('profile.pet.vaccinationRecords')}
      </Typography>
      <Box
        {...getRootProps()}
        sx={{
          alignItems: 'center',
          backgroundColor: theme.palette.grey[100],
          borderRadius: 1,
          borderStyle: 'dashed',
          borderWidth: 1,
          borderColor: theme.palette.grey[400],
          display: 'flex',
          height: 148,
          justifyContent: 'center',
        }}
      >
        <input {...getInputProps()} />

        <Stack
          spacing={2}
          alignItems="center"
          sx={{
            width: !isGreaterThanMD ? '90%' : 'auto',
          }}
        >
          {isGreaterThanMD && (
            <Stack alignItems="center" spacing={0.5}>
              <Typography variant="subtitle1" fontWeight={600}>
                {t('profile.pet.vaccinationRecords.upload.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('profile.pet.form.upload.subtitle')}
              </Typography>
            </Stack>
          )}
          <Button variant="outlined" onClick={open} fullWidth={!isGreaterThanMD}>
            {t('profile.pet.vaccinationRecords.upload.chooseFile')}
          </Button>
        </Stack>
      </Box>

      {files.length > 0 && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
          <InfoOutlinedIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
          <Typography variant="body2" color="text.secondary">
            {t('profile.pet.vaccinationRecords.upload.saveReminder')}
          </Typography>
        </Stack>
      )}

      {files.map((file, index) => (
        <Stack
          key={`${file.name}-${index}`}
          direction="row"
          spacing={2}
          alignItems="center"
          py={1.5}
          px={2}
          mt={2}
          sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}
        >
          <Stack flex={1}>
            <Typography variant="body1">{file.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {formatFileSize(file.size)}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <WarningAmberIcon fontSize="small" color="warning" />
            <Typography variant="body2" sx={{ color: theme.palette.warning.main }}>
              {t('profile.pet.vaccinationRecords.unsaved')}
            </Typography>
          </Stack>
          <IconButton onClick={() => onRemoveHandler(file)} aria-label={t('global.label.remove')}>
            <DeleteOutlineIcon />
          </IconButton>
        </Stack>
      ))}

      {records.map((attachment) => (
        <Stack
          key={attachment.id}
          direction="row"
          spacing={2}
          alignItems="center"
          py={1.5}
          px={2}
          mt={2}
          sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}
        >
          <Typography variant="body1" flex={1}>
            <Link
              href={imageResizer({ bucket: attachment?.asset?.bucket, key: attachment?.asset?.key })}
              target="_blank"
              rel="noreferrer"
            >
              {attachment.asset?.displayName || 'unknown'}
            </Link>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'lowercase' }}>{`${t(
            'global.label.added'
          )} ${formatDateSimple(new Date(attachment.createdAt!))}`}</Typography>
        </Stack>
      ))}
    </>
  )
}
